/**
 * Push-notification service — vanilla TS, no React dependency.
 *
 * Responsibilities:
 *   1. Detect browser support and current Notification permission state.
 *   2. Register `/firebase-messaging-sw.js` once per origin.
 *   3. Request permission (only inside a user gesture — caller's job).
 *   4. Call `getToken({ vapidKey, serviceWorkerRegistration })` and cache
 *      the FCM token in-memory + localStorage.
 *   5. Attach an `onMessage` listener that re-broadcasts foreground messages
 *      as a `myu:push-message` custom DOM event.
 *   6. Listen for the SW `myu:notification-click` postMessage so the SPA
 *      can route the user instead of doing a full reload.
 *   7. Tear everything down on logout (`deleteToken`, clear caches).
 *
 * NOTE: there is intentionally NO dedicated backend endpoint for token
 * upsert/revoke anymore. The token is delivered to the backend as part of
 * the `PUT /<userservice>/api/<version>/user/updateinfo/:userId` call
 * (`deviceToken` / `chatDeviceToken`) which runs on every login.
 *
 * Token refresh: the modular Firebase SDK no longer exposes `onTokenRefresh`
 * (it was removed in v9). The supported pattern is to re-call `getToken()`
 * periodically — we do this on every cold start and after the page is
 * brought back online. Because `updateDeviceInfo` re-sends the latest token
 * on every login, no separate dedup endpoint is needed.
 */

import { getFirebaseEnv, getMessagingIfSupported, getVapidKey } from './firebase';
import {
  PUSH_MESSAGE_EVENT,
  PUSH_STORAGE_KEYS,
  type IncomingPushMessage,
  type PushPermission,
  type PushSupport,
} from './types';

// ---------------------------------------------------------------------------
// Capability detection
// ---------------------------------------------------------------------------

const SW_URL = '/firebase-messaging-sw.js';

export function detectSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported-browser';
  if (!('serviceWorker' in navigator)) return 'unsupported-browser';
  if (!('Notification' in window)) return 'unsupported-browser';
  if (!('PushManager' in window)) return 'unsupported-browser';
  // Service workers require a secure context. `localhost` counts as secure.
  if (!window.isSecureContext) return 'unsupported-context';
  return 'supported';
}

export function getPermission(): PushPermission {
  if (detectSupport() !== 'supported') return 'unsupported';
  return Notification.permission as PushPermission;
}

// ---------------------------------------------------------------------------
// Local-storage helpers (kept tiny so the SSR-less assumption is obvious)
// ---------------------------------------------------------------------------

const ls = {
  get: (k: string) => (typeof window === 'undefined' ? null : window.localStorage.getItem(k)),
  set: (k: string, v: string) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(k, v);
  },
  remove: (k: string) => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(k);
  },
};

// ---------------------------------------------------------------------------
// Tiny state-change emitter (so React can re-render without Zustand)
// ---------------------------------------------------------------------------

type StateListener = () => void;
const listeners = new Set<StateListener>();
function emit() {
  for (const l of listeners) {
    try {
      l();
    } catch {
      /* listeners must not throw */
    }
  }
}
export function subscribe(listener: StateListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Service worker registration
// ---------------------------------------------------------------------------

let swRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null;

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = (async () => {
      // Re-use an existing registration if the SW is already alive.
      const existing = await navigator.serviceWorker.getRegistration(SW_URL);
      if (existing) {
        // Explicitly check for updates on every authenticated startup. Relying
        // on the browser's periodic check can leave a broken push worker active
        // for up to 24 hours after a deployment or local-code fix.
        await existing.update().catch(() => undefined);
        await navigator.serviceWorker.ready;
        return existing;
      }
      const reg = await navigator.serviceWorker.register(SW_URL, {
        scope: '/',
        updateViaCache: 'none',
      });
      // Wait until it's actually active so the first getToken() doesn't race.
      if (reg.installing) {
        await new Promise<void>((resolve) => {
          const sw = reg.installing!;
          const handler = () => {
            if (sw.state === 'activated' || sw.state === 'redundant') {
              sw.removeEventListener('statechange', handler);
              resolve();
            }
          };
          sw.addEventListener('statechange', handler);
        });
      }
      await navigator.serviceWorker.ready;
      return reg;
    })();
  }
  return swRegistrationPromise;
}

// ---------------------------------------------------------------------------
// Foreground message listener
// ---------------------------------------------------------------------------

let foregroundUnsubscribe: (() => void) | null = null;
let swMessageHandler: ((e: MessageEvent) => void) | null = null;

// ---------------------------------------------------------------------------
// Incoming-message diagnostics
// ---------------------------------------------------------------------------
// Every push that reaches the page (foreground onMessage OR background-click
// via the SW) is logged with a clear prefix and a classified "kind" so the
// console shows e.g.
//
//   [push] received  ▸ message · foreground   { title, body, data, url, ... }
//
// The last `RECENT_MESSAGE_BUFFER_SIZE` payloads are kept in memory so the
// `__myuPush.history()` dev helper can dump them on demand.
// ---------------------------------------------------------------------------

const RECENT_MESSAGE_BUFFER_SIZE = 25;
const recentMessages: IncomingPushMessage[] = [];

/**
 * Derive a short, human-readable "kind" from the payload. The backend is
 * expected to set `data.type` (e.g. `'message'`, `'post-comment'`,
 * `'class-invite'`) so we use that when available; otherwise fall back to
 * `data.category`, `data.kind`, or `'unknown'`.
 */
function inferMessageKind(data: Record<string, string>): string {
  return data.type || data.category || data.kind || data.notificationType || 'unknown';
}

function recordIncomingMessage(message: IncomingPushMessage): void {
  const kind = inferMessageKind(message.data);

  // Push into the ring buffer (newest first so `history()` is easy to read).
  recentMessages.unshift(message);
  if (recentMessages.length > RECENT_MESSAGE_BUFFER_SIZE) {
    recentMessages.length = RECENT_MESSAGE_BUFFER_SIZE;
  }

  // ALWAYS log — push problems and unexpected payload shapes are otherwise
  // invisible. Use `console.groupCollapsed` so the line stays compact but
  // the full payload is one click away.
  if (typeof console !== 'undefined') {
    const header = `[push] received ▸ ${kind} · ${message.source}`;
    if (typeof console.groupCollapsed === 'function') {
      console.groupCollapsed(header, message.title);
      console.log('title :', message.title);
      console.log('body  :', message.body);
      console.log('url   :', message.url ?? '(none)');
      console.log('tag   :', message.tag ?? '(none)');
      console.log('data  :', message.data);
      console.log('full  :', message);
      console.groupEnd();
    } else {
      console.log(header, message);
    }
  }
}

/** Snapshot of the recent-message buffer (newest first). */
export function getRecentMessages(): readonly IncomingPushMessage[] {
  return recentMessages.slice();
}

async function attachForegroundListener() {
  if (foregroundUnsubscribe) return;

  const messaging = await getMessagingIfSupported();
  if (!messaging) return;

  const { onMessage } = await import('firebase/messaging');
  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    console.info('[push] FCM FOREGROUND MESSAGE RECEIVED', payload);
    const data = (payload.data ?? {}) as Record<string, string>;

    // MyU backend wraps the actual notification in `data.message` as a JSON
    // string. Unwrap it so the in-app toast (and the OS notification we
    // surface below) show real content instead of generic fallbacks.
    let inner: Record<string, unknown> = {};
    if (typeof data.message === 'string' && data.message.length > 0) {
      try {
        const parsed = JSON.parse(data.message);
        if (parsed && typeof parsed === 'object') inner = parsed as Record<string, unknown>;
      } catch {
        /* malformed envelope — fall through to generic fields. */
      }
    }
    const actor = (inner.whoFiredUserBasicDetail ?? {}) as {
      name?: string;
      surName?: string;
      username?: string;
      profileImage?: string;
      profileImageThumbnail?: string;
    };
    const actorName =
      [actor.name, actor.surName].filter(Boolean).join(' ').trim() || actor.username || '';

    const title =
      payload.notification?.title ??
      (inner.title as string | undefined) ??
      data.title ??
      actorName ??
      'New notification';
    const body =
      payload.notification?.body ?? (inner.body as string | undefined) ?? data.body ?? '';
    const icon = actor.profileImageThumbnail || actor.profileImage || '/branding/myu-logo-mark.png';

    const message: IncomingPushMessage = {
      title,
      body,
      url: (inner.url as string | undefined) ?? data.url,
      data,
      tag: data.tag ?? payload.messageId,
      receivedAt: Date.now(),
      source: 'foreground',
    };
    recordIncomingMessage(message);
    window.dispatchEvent(new CustomEvent(PUSH_MESSAGE_EVENT, { detail: message }));

    // ALSO surface an OS-level browser notification for every foreground
    // push. The FCM SDK intentionally skips the OS popup when `onMessage`
    // fires (it assumes the app will render its own UI), so without this
    // line users would only see our in-app toast — they'd miss the push
    // entirely if their attention was anywhere else on screen.
    //
    // The SW (`onBackgroundMessage`) handles the case where the tab is
    // closed; this branch covers tab-open (focused or hidden).
    void showForegroundOsNotification(title, body, icon, message);
  });

  // The SW forwards background pushes immediately for live badge updates,
  // and posts clicks back so React Router can navigate in-app.
  if (!swMessageHandler && 'serviceWorker' in navigator) {
    swMessageHandler = (event: MessageEvent) => {
      if (
        event.data?.type !== 'myu:notification-click' &&
        event.data?.type !== 'myu:notification-received'
      ) {
        return;
      }
      console.info(`[push] SERVICE WORKER ${String(event.data.type).toUpperCase()}`, event.data);
      const detail = event.data.message as IncomingPushMessage;
      recordIncomingMessage(detail);
      window.dispatchEvent(new CustomEvent(PUSH_MESSAGE_EVENT, { detail }));
    };
    navigator.serviceWorker.addEventListener('message', swMessageHandler);
  }
}

/**
 * Render an OS-level (browser/system tray) notification from the page when
 * a foreground `onMessage` arrives. The FCM SDK does NOT show OS popups for
 * foreground messages by design, so without this helper users would only
 * see our in-app toast.
 *
 * Uses the existing SW registration so the notification is properly tied
 * to our origin, is clickable (routes via `notificationclick` in the SW),
 * and survives the page reload.
 *
 * Caller is expected to have permission === 'granted'; we short-circuit
 * otherwise rather than throwing.
 */
async function showForegroundOsNotification(
  title: string,
  body: string,
  icon: string,
  message: IncomingPushMessage,
): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ??
      (await navigator.serviceWorker.ready);
    if (!registration) return;
    // `renotify` is widely supported in Chrome/Edge but missing from TS's
    // `NotificationOptions` type — cast through `unknown` so the call type-
    // checks without us shipping a global `lib.dom` augmentation.
    await registration.showNotification(title, {
      body,
      icon,
      badge: '/branding/myu-logo-mark.png',
      tag: message.tag,
      // When two pushes share a `tag` (e.g. several comments on the same
      // post), the browser collapses them. `renotify: true` makes the new
      // one re-pop so the user actually sees it.
      renotify: Boolean(message.tag),
      requireInteraction: true,
      data: { ...message.data, url: message.url ?? '/notifications' },
    } as NotificationOptions & { renotify?: boolean; requireInteraction?: boolean });
    if (import.meta.env.DEV) {
      console.info(
        `[push] ✓ registration.showNotification("${title}") resolved — if you don't see an OS popup within 2s, check macOS Settings → Notifications → [browser] → Allow + Alerts.`,
      );
    }

    // Belt-and-braces fallback: also try the page-context Notification
    // constructor. On some Chrome configurations the SW-issued popup is
    // silently dropped (e.g. SW not yet controlling the page on first
    // load), and this second path goes through a different renderer.
    // The browser de-dupes by `tag`, so this won't double-show when both
    // paths work.
    try {
      const n = new Notification(title, {
        body,
        icon,
        tag: message.tag,
        data: { ...message.data, url: message.url ?? '/notifications' },
      });
      n.onclick = () => {
        window.focus();
        const target = message.url ?? '/notifications';
        if (window.location.pathname !== target) window.location.href = target;
        n.close();
      };
      if (import.meta.env.DEV) {
        console.info('[push] ✓ new Notification() fallback also fired');
      }
    } catch (fallbackErr) {
      if (import.meta.env.DEV) {
        console.warn('[push] new Notification() fallback threw', fallbackErr);
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[push] showForegroundOsNotification failed', err);
    }
  }
}

function detachForegroundListener() {
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = null;
  if (swMessageHandler && 'serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', swMessageHandler);
    swMessageHandler = null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type EnableStatus = 'enabled' | 'denied' | 'unsupported' | 'error';

export interface EnableResult {
  status: EnableStatus;
  token?: string;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Token prefetch — runs WITHOUT a userId so the login screen can warm the
// SW + cache the FCM token while the user is still typing their password.
// `updateDeviceInfo` (called right after the login mutation succeeds) then
// finds the token instantly instead of POSTing `'no-token'` to the backend.
// ---------------------------------------------------------------------------

let prefetchTokenPromise: Promise<string | null> | null = null;
let cachedFcmToken: string | null = null;

/**
 * Possible reasons `prefetchToken()` returned `null`. Kept around so
 * `tryGetFcmToken()` (or a manual `window.__myuPush.status()` call from the
 * console) can surface the real error instead of an opaque "no-token".
 */
export type PrefetchFailureReason =
  | 'unsupported-browser'
  | 'unsupported-context'
  | 'permission-not-granted'
  | 'vapid-key-missing'
  | 'sw-registration-failed'
  | 'messaging-not-supported'
  | 'gettoken-empty'
  | 'gettoken-threw';

let lastFailureReason: PrefetchFailureReason | null = null;
let lastFailureError: unknown = null;

function rememberFailure(reason: PrefetchFailureReason, error: unknown = null) {
  lastFailureReason = reason;
  lastFailureError = error;
  // Always log — push problems are common in dev (missing VAPID, wrong SW
  // config) and silent failures make them invisible. Prefixed so the line is
  // easy to grep in the console.
  if (typeof console !== 'undefined') {
    if (error) {
      console.warn(`[push] prefetchToken → null (${reason})`, error);
    } else {
      console.warn(`[push] prefetchToken → null (${reason})`);
    }
  }
}

export function getLastPrefetchFailure(): {
  reason: PrefetchFailureReason | null;
  error: unknown;
} {
  return { reason: lastFailureReason, error: lastFailureError };
}

/**
 * Best-effort FCM token bootstrap. Idempotent: subsequent calls return the
 * same in-flight promise so it's safe to call from anywhere (the gate, the
 * login mutation, settings page, …).
 *
 * Requires `Notification.permission === 'granted'` — does NOT prompt. The
 * caller is expected to have collected permission already (e.g. via the
 * pre-login `usePushPermissionGate`).
 *
 * Returns `null` (not throws) on any failure so the auth flow keeps moving.
 * Call `getLastPrefetchFailure()` to find out why.
 */
export async function prefetchToken(): Promise<string | null> {
  if (cachedFcmToken) {
    console.info('[push] token request skipped — cached token available', {
      tokenPrefix: `${cachedFcmToken.slice(0, 12)}…`,
    });
    return cachedFcmToken;
  }
  if (prefetchTokenPromise) {
    console.info('[push] token request joined existing request');
    return prefetchTokenPromise;
  }

  prefetchTokenPromise = (async () => {
    try {
      const support = detectSupport();
      const firebaseEnv = getFirebaseEnv();
      console.info('[push] token request starting', {
        support,
        permission: typeof Notification === 'undefined' ? 'unavailable' : Notification.permission,
        secureContext: window.isSecureContext,
        projectId: firebaseEnv?.projectId ?? '(missing)',
        messagingSenderId: firebaseEnv?.messagingSenderId ?? '(missing)',
        vapidConfigured: Boolean(firebaseEnv?.vapidKey),
      });
      if (support !== 'supported') {
        rememberFailure(support as PrefetchFailureReason);
        return null;
      }
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        rememberFailure('permission-not-granted');
        return null;
      }
      const vapidKey = getVapidKey();
      if (!vapidKey) {
        rememberFailure('vapid-key-missing');
        return null;
      }

      let registration: ServiceWorkerRegistration;
      try {
        registration = await ensureServiceWorker();
        console.info('[push] service worker ready', {
          scope: registration.scope,
          state: registration.active?.state ?? registration.installing?.state ?? 'unknown',
          scriptURL: registration.active?.scriptURL ?? registration.installing?.scriptURL,
        });
      } catch (err) {
        rememberFailure('sw-registration-failed', err);
        return null;
      }

      const messaging = await getMessagingIfSupported();
      if (!messaging) {
        rememberFailure('messaging-not-supported');
        return null;
      }

      const { getToken } = await import('firebase/messaging');
      let token: string;
      try {
        token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: registration,
        });
      } catch (err) {
        rememberFailure('gettoken-threw', err);
        return null;
      }
      if (!token) {
        rememberFailure('gettoken-empty');
        return null;
      }
      cachedFcmToken = token;
      lastFailureReason = null;
      lastFailureError = null;
      console.info('[push] FCM token created', {
        projectId: firebaseEnv?.projectId,
        tokenPrefix: `${token.slice(0, 12)}…`,
        tokenLength: token.length,
      });
      // Attach the foreground onMessage listener now that we know push works.
      // Without this the page never hears `onMessage` until the user opens
      // some other code path that calls `enable()`, so foreground pushes are
      // silently dropped.
      void attachForegroundListener();
      return cachedFcmToken;
    } finally {
      // Allow a retry the next time someone asks — but keep `cachedFcmToken`
      // around so successful calls short-circuit immediately.
      prefetchTokenPromise = null;
    }
  })();

  return prefetchTokenPromise;
}

/**
 * Synchronous accessor — returns whatever `prefetchToken()` last resolved
 * to, or `null` if it hasn't been called yet. Useful for code that must
 * stay synchronous (e.g. building a request body during render).
 */
export function getCachedToken(): string | null {
  return cachedFcmToken;
}

/**
 * Request notification permission, fetch a token, register it with the
 * backend, and start listening for messages.
 *
 * MUST be called inside a user gesture handler (button click). Browsers
 * block `Notification.requestPermission()` otherwise.
 */
export async function enable(opts: { userId: string }): Promise<EnableResult> {
  const support = detectSupport();
  if (support !== 'supported') return { status: 'unsupported', reason: support };

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    return {
      status: 'error',
      reason: 'VAPID key not configured (VITE_FIREBASE_VAPID_KEY missing)',
    };
  }

  // 1. Permission
  let permission: PushPermission = Notification.permission as PushPermission;
  if (permission === 'default') {
    try {
      permission = (await Notification.requestPermission()) as PushPermission;
    } catch (err) {
      emit();
      return {
        status: 'error',
        reason: err instanceof Error ? err.message : 'Notification permission request failed',
      };
    }
  }
  // Permission can succeed even if service-worker registration or FCM token
  // creation fails. Notify React immediately so the button never remains in
  // a stale "default" state after the browser prompt closes.
  emit();
  if (permission !== 'granted') {
    ls.set(PUSH_STORAGE_KEYS.optedOut, 'true');
    emit();
    return { status: 'denied' };
  }
  ls.remove(PUSH_STORAGE_KEYS.optedOut);

  // 2. Service worker + token (reuses the gate's prefetch when available).
  let registration: ServiceWorkerRegistration;
  try {
    registration = await ensureServiceWorker();
  } catch (err) {
    return {
      status: 'error',
      reason: err instanceof Error ? err.message : 'Service worker registration failed',
    };
  }
  let token = cachedFcmToken;
  if (!token) {
    token = await prefetchToken();
  }
  if (!token) {
    const messaging = await getMessagingIfSupported();
    if (!messaging) return { status: 'unsupported', reason: 'messaging-not-supported' };
    const { getToken } = await import('firebase/messaging');
    try {
      token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
    } catch (err) {
      return {
        status: 'error',
        reason: err instanceof Error ? err.message : 'Notification token creation failed',
      };
    }
  }
  if (!token) {
    return { status: 'error', reason: 'getToken returned empty' };
  }
  cachedFcmToken = token;

  // 3. Tell the SW about the user so background clicks can route correctly.
  registration.active?.postMessage({ type: 'myu:user', userId: opts.userId });

  // 4. Persist the token locally so `tryGetFcmToken()` (used by
  //    `updateDeviceInfo`) and other callers can read it on the next cold
  //    start without re-running `getToken()`. Delivery to the backend
  //    happens via `PUT /user/updateinfo/:id` — there is no separate
  //    `/notification/push/tokens` upsert anymore.
  ls.set(PUSH_STORAGE_KEYS.lastRegisteredToken, token);
  ls.set(PUSH_STORAGE_KEYS.lastRegisteredAt, new Date().toISOString());

  // 5. Foreground listener
  await attachForegroundListener();
  emit();

  return { status: 'enabled', token };
}

/**
 * Idempotent revalidation. Call on every cold start / route change while
 * logged in. Fast no-op when permission is `default` or the token is current.
 */
export async function refreshIfEnabled(opts: { userId: string }): Promise<void> {
  if (detectSupport() !== 'supported') return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (ls.get(PUSH_STORAGE_KEYS.optedOut) === 'true') return;
  try {
    await enable(opts);
  } catch (err) {
    // Network blips are tolerated — we'll retry next cold start.
    if (import.meta.env.DEV) console.warn('[push] refresh failed', err);
  }
}

/** Disable on logout. Drops the FCM token at Firebase and clears caches. */
export async function disable(): Promise<void> {
  detachForegroundListener();

  const messaging = await getMessagingIfSupported().catch(() => null);
  if (messaging) {
    try {
      const { deleteToken } = await import('firebase/messaging');
      await deleteToken(messaging);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[push] deleteToken failed', err);
    }
  }

  ls.remove(PUSH_STORAGE_KEYS.lastRegisteredToken);
  ls.remove(PUSH_STORAGE_KEYS.lastRegisteredAt);
  cachedFcmToken = null;
  prefetchTokenPromise = null;
  emit();
}

/** True when the user has dismissed our prompt — used to avoid re-pestering. */
export function hasOptedOut(): boolean {
  return ls.get(PUSH_STORAGE_KEYS.optedOut) === 'true';
}

export function markOptedOut(): void {
  ls.set(PUSH_STORAGE_KEYS.optedOut, 'true');
  emit();
}

/** The token we last successfully registered with the backend (if any). */
export function getRegisteredToken(): string | null {
  return ls.get(PUSH_STORAGE_KEYS.lastRegisteredToken);
}

export const pushService = {
  detectSupport,
  getPermission,
  enable,
  disable,
  refreshIfEnabled,
  prefetchToken,
  getCachedToken,
  getLastPrefetchFailure,
  getRecentMessages,
  hasOptedOut,
  markOptedOut,
  getRegisteredToken,
  subscribe,
};

export type PushService = typeof pushService;

// ---------------------------------------------------------------------------
// Dev-only console helper
// ---------------------------------------------------------------------------
// Run `__myuPush.status()` in the browser console to dump everything we
// know about the current push state — handy when the post-login PUT keeps
// sending `deviceToken: 'no-token'` and you need to figure out which step
// of the prefetch is failing.
//
// Run `__myuPush.history()` to dump every push payload received this
// session as a sortable console.table.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __myuPush?: unknown }).__myuPush = {
    status: () => {
      const status = {
        support: detectSupport(),
        permission: getPermission(),
        vapidKey: getVapidKey() ? '(set)' : '(MISSING — check VITE_FIREBASE_VAPID_KEY)',
        cachedToken: cachedFcmToken
          ? `${cachedFcmToken.slice(0, 12)}… (length ${cachedFcmToken.length})`
          : null,
        lastFailure: getLastPrefetchFailure(),
        registeredTokenInStorage: ls.get(PUSH_STORAGE_KEYS.lastRegisteredToken) ? '(set)' : null,
        recentMessageCount: recentMessages.length,
      };
      console.table(status);
      return status;
    },
    prefetch: () => prefetchToken(),
    enable: (userId: string) => enable({ userId }),
    disable: () => disable(),
    /** Wipe the in-memory cache and try again. */
    reset: () => {
      cachedFcmToken = null;
      prefetchTokenPromise = null;
      lastFailureReason = null;
      lastFailureError = null;
      console.info('[push] in-memory cache cleared');
    },
    /** Dump every push received this session (newest first). */
    history: () => {
      if (recentMessages.length === 0) {
        console.info('[push] no messages received yet');
        return [];
      }
      const rows = recentMessages.map((m) => ({
        kind: inferMessageKind(m.data),
        source: m.source,
        title: m.title,
        body: m.body,
        url: m.url ?? '',
        receivedAt: new Date(m.receivedAt).toISOString(),
      }));
      console.table(rows);
      return recentMessages.slice();
    },
    clearHistory: () => {
      recentMessages.length = 0;
      console.info('[push] history cleared');
    },

    // -------------------------------------------------------------------
    // Diagnostic helpers — for "the notification isn't appearing" reports
    // -------------------------------------------------------------------

    /**
     * Fire an OS notification DIRECTLY via the service worker, bypassing
     * FCM entirely. If you don't see a system-tray popup after this:
     *   ✗ the problem is OS-level (macOS Focus / Chrome notification
     *     setting / OS notification disabled for the browser).
     *   ✓ Notifications work — the problem is in the push pipeline; check
     *     `__myuPush.simulatePush()` or `__myuPush.history()` next.
     *
     * Returns a Promise<boolean> — true if showNotification resolved, false
     * otherwise. Always logs the outcome.
     */
    testNotification: async (overrides?: { title?: string; body?: string }) => {
      const title = overrides?.title ?? 'MyU test notification';
      const body =
        overrides?.body ??
        'If you can read this, browser notifications are working. The push pipeline is the problem.';
      if (typeof Notification === 'undefined') {
        console.error('[push] testNotification: Notification API unavailable');
        return false;
      }
      if (Notification.permission !== 'granted') {
        console.error(
          `[push] testNotification: permission is '${Notification.permission}' (need 'granted')`,
        );
        return false;
      }
      if (!('serviceWorker' in navigator)) {
        console.error('[push] testNotification: no service worker support');
        return false;
      }
      try {
        const reg =
          (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ??
          (await navigator.serviceWorker.ready);
        if (!reg) {
          console.error('[push] testNotification: no SW registration found');
          return false;
        }
        await reg.showNotification(title, {
          body,
          icon: '/branding/myu-logo-mark.png',
          badge: '/branding/myu-logo-mark.png',
          tag: 'myu-test-' + Date.now(),
          requireInteraction: true,
          data: { url: '/notifications', test: true },
        } as NotificationOptions & { renotify?: boolean });
        console.info(
          '[push] testNotification ✓ — showNotification resolved. If no popup appeared, check macOS Settings → Notifications → ' +
            (navigator.userAgent.includes('Chrome') ? 'Chrome' : 'your browser') +
            ' → Allow / Alerts.',
        );
        return true;
      } catch (err) {
        console.error('[push] testNotification ✗ — showNotification threw', err);
        return false;
      }
    },

    /**
     * Simulate a real FCM push end-to-end. Triggers the same code path as
     * a foreground `onMessage` would — parser, in-app toast, query
     * invalidation, AND the OS-level notification. Use this to verify the
     * whole pipeline without waiting for the backend.
     *
     * Example:
     *   __myuPush.simulatePush()                       // default sample
     *   __myuPush.simulatePush({ type: 'Follow', body: 'Sarah followed you' })
     */
    simulatePush: (overrides?: {
      type?: string;
      title?: string | null;
      body?: string;
      url?: string | null;
      actorName?: string;
      objectDataId?: number;
    }) => {
      const inner = {
        type: overrides?.type ?? 'NewsCommentMention',
        title: overrides?.title ?? null,
        body: overrides?.body ?? 'Test push simulated via __myuPush.simulatePush()',
        url: overrides?.url ?? null,
        objectDataId: overrides?.objectDataId ?? 12345,
        userIdToNotify: 0,
        whoFiredUserBasicDetail: {
          id: 1,
          name: overrides?.actorName ?? 'Test User',
          surName: '',
          username: 'testuser',
          profileImage: '',
          profileImageThumbnail: '',
        },
        notificationCategory: 'you',
        messageId: 'simulated-' + Date.now(),
      };
      const message: IncomingPushMessage = {
        title: inner.title || inner.whoFiredUserBasicDetail.name,
        body: inner.body,
        url: inner.url ?? undefined,
        data: { message: JSON.stringify(inner) },
        tag: inner.messageId,
        receivedAt: Date.now(),
        source: 'foreground',
      };
      recordIncomingMessage(message);
      window.dispatchEvent(new CustomEvent(PUSH_MESSAGE_EVENT, { detail: message }));
      void showForegroundOsNotification(
        message.title,
        message.body,
        '/branding/myu-logo-mark.png',
        message,
      );
      console.info('[push] simulatePush dispatched —', inner);
    },

    /** Report which service worker is active (helps spot stale SWs). */
    swInfo: async () => {
      if (!('serviceWorker' in navigator)) {
        console.warn('[push] no service worker support');
        return null;
      }
      const all = await navigator.serviceWorker.getRegistrations();
      const info = all.map((r) => ({
        scope: r.scope,
        scriptUrl:
          (r.active && r.active.scriptURL) ||
          (r.waiting && r.waiting.scriptURL) ||
          (r.installing && r.installing.scriptURL) ||
          '(none)',
        state:
          (r.active && r.active.state) ||
          (r.waiting && 'waiting') ||
          (r.installing && 'installing') ||
          '(unknown)',
      }));
      console.table(info);
      console.info(
        '[push] If the active SW scriptURL ends in /firebase-messaging-sw.js and you don\'t see the latest SW_VERSION line in the SW console, click "Unregister" in DevTools → Application → Service Workers, close all tabs, then reopen.',
      );
      return info;
    },

    /** Unregister every SW for this origin so the next reload installs fresh. */
    unregisterAll: async () => {
      if (!('serviceWorker' in navigator)) return false;
      const all = await navigator.serviceWorker.getRegistrations();
      for (const r of all) await r.unregister();
      console.info(
        `[push] Unregistered ${all.length} SW(s). Close ALL tabs of this site and reopen for the new SW to install.`,
      );
      return true;
    },

    /**
     * One-call mega-diagnostic. Runs every check that can possibly explain
     * "the notification doesn't appear" and prints a clear pass/fail table.
     * Also fires a real test notification at the end so the user can
     * compare what the code did vs what actually rendered.
     */
    diagnose: async () => {
      console.group('%c[push] DIAGNOSIS', 'color:#00A7C8;font-weight:bold');

      const checks: Array<{ check: string; result: string; hint?: string }> = [];

      // 1. Browser capability
      const support = detectSupport();
      checks.push({
        check: 'Browser support',
        result: support === 'supported' ? '✓ supported' : `✗ ${support}`,
        hint: support === 'supported' ? '' : 'Use Chrome / Edge / Firefox on desktop.',
      });

      // 2. Permission
      const perm = getPermission();
      checks.push({
        check: 'Notification.permission',
        result: perm === 'granted' ? `✓ ${perm}` : `✗ ${perm}`,
        hint: perm === 'granted' ? '' : 'Click "Enable notifications" on the login screen.',
      });

      // 3. VAPID key
      const vapid = getVapidKey();
      checks.push({
        check: 'VITE_FIREBASE_VAPID_KEY',
        result: vapid ? '✓ set' : '✗ MISSING',
        hint: vapid ? '' : 'Add VITE_FIREBASE_VAPID_KEY to .env and restart vite.',
      });

      // 4. FCM token
      let token: string | null = cachedFcmToken;
      if (!token) token = await prefetchToken();
      checks.push({
        check: 'FCM token',
        result: token ? `✓ ${token.slice(0, 16)}…` : '✗ null',
        hint: token ? '' : `prefetch failed: ${JSON.stringify(getLastPrefetchFailure())}`,
      });

      // 5. SW registration
      let swReg: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      }
      const swState =
        swReg?.active?.state ??
        (swReg?.waiting ? 'waiting' : swReg?.installing ? 'installing' : 'none');
      checks.push({
        check: 'SW registration',
        result: swReg && swReg.active ? `✓ ${swState}` : `✗ ${swState}`,
        hint: swReg?.active ? '' : 'Run __myuPush.unregisterAll(), close all tabs, reopen.',
      });

      // 6. Is the SW controlling this page?
      const controller = navigator.serviceWorker?.controller;
      checks.push({
        check: 'SW controls this page',
        result: controller ? '✓ yes' : '⚠ no',
        hint: controller
          ? ''
          : 'First page load after install — reload once and the SW will control the page.',
      });

      // 7. Tab visibility
      const visibility = typeof document !== 'undefined' ? document.visibilityState : 'unknown';
      checks.push({
        check: 'document.visibilityState',
        result: visibility === 'visible' ? `${visibility} (foreground onMessage path)` : visibility,
      });

      // 8. Permissions API (more accurate than Notification.permission on some platforms)
      let permApi = '(unavailable)';
      try {
        if ('permissions' in navigator) {
          const status = await navigator.permissions.query({
            name: 'notifications' as PermissionName,
          });
          permApi = status.state;
        }
      } catch {
        /* not all browsers expose this */
      }
      checks.push({
        check: 'Permissions API notifications',
        result:
          permApi === 'granted'
            ? `✓ ${permApi}`
            : permApi === '(unavailable)'
              ? `⚠ ${permApi}`
              : `✗ ${permApi}`,
      });

      console.table(checks);

      console.info(
        '%cFiring a test notification in 500ms — watch for an OS popup',
        'color:#00A7C8',
      );
      console.info(
        'If you DON\'T see the popup but the log below says "✓ resolved", the OS or browser is blocking notifications:\n' +
          '  • macOS: System Settings → Notifications → [browser] → Allow Notifications: ON, Alert style: Alerts.\n' +
          '  • macOS: Disable any active Focus mode.\n' +
          '  • Chrome: chrome://settings/content/notifications — localhost must NOT be in Blocked.\n' +
          '  • Not Incognito / Private mode.',
      );
      console.groupEnd();

      await new Promise((r) => setTimeout(r, 500));
      const testHandle = (
        window as unknown as {
          __myuPush?: {
            testNotification?: (o?: { title?: string; body?: string }) => Promise<boolean>;
          };
        }
      ).__myuPush;
      if (testHandle?.testNotification) {
        await testHandle.testNotification({
          title: '[diagnose] MyU notification test',
          body:
            'If you can see this popup, notifications work — the push pipeline is the problem. ' +
            'If not, check your OS / browser notification settings.',
        });
      }

      return checks;
    },
  };
}
